type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN'

interface CircuitEntry {
  state: CircuitState
  failures: number
  openedAt: number | null
}

/**
 * CircuitBreaker — opens after repeated failures; auto half-opens after cooldown.
 */
export class CircuitBreaker {
  private readonly circuits = new Map<string, CircuitEntry>()
  private readonly failureThreshold: number
  private readonly openMs: number

  constructor(failureThreshold = 5, openMs = 30_000) {
    this.failureThreshold = failureThreshold
    this.openMs = openMs
  }

  assertClosed(supplierId: string): void {
    const entry = this.get(supplierId)
    if (entry.state === 'OPEN') {
      if (entry.openedAt && Date.now() - entry.openedAt >= this.openMs) {
        entry.state = 'HALF_OPEN'
        this.circuits.set(supplierId, entry)
        return
      }
      throw new Error(`Circuit open para ${supplierId}`)
    }
  }

  recordSuccess(supplierId: string): void {
    this.circuits.set(supplierId, { state: 'CLOSED', failures: 0, openedAt: null })
  }

  recordFailure(supplierId: string): void {
    const entry = this.get(supplierId)
    entry.failures += 1
    if (entry.failures >= this.failureThreshold || entry.state === 'HALF_OPEN') {
      entry.state = 'OPEN'
      entry.openedAt = Date.now()
    }
    this.circuits.set(supplierId, entry)
  }

  getState(supplierId: string): CircuitState {
    return this.get(supplierId).state
  }

  reset(supplierId?: string): void {
    if (supplierId) this.circuits.delete(supplierId)
    else this.circuits.clear()
  }

  private get(supplierId: string): CircuitEntry {
    return (
      this.circuits.get(supplierId) ?? {
        state: 'CLOSED',
        failures: 0,
        openedAt: null,
      }
    )
  }
}

export const circuitBreaker = new CircuitBreaker()
